import * as go from 'gojs';
import type { WarningPanelInterface } from 'gojs/panelData';
import { getFont } from '../utils/font';
import { getTextStroke } from './baseUnitUtils';
import type { Severity } from '../../../../util/warnings';
import { getSeverityColor, getSeverityTitle } from '../../../../util/warnings';

const $ = go.GraphObject.make;

const visible = (dataField: DataField) =>
  new go.Binding('visible', dataField, (warnings) => {
    if (!warnings[0].warnings) {
      return false;
    }
    return warnings[0].warnings.length > 0;
  });

type DataField = 'majorWarnings' | 'moderateWarnings' | 'minorWarnings';

export const Warnings = (dataField: DataField, severity: Severity) =>
  $(
    go.Panel,
    'Vertical',
    {
      alignment: go.Spot.Left,
      stretch: go.GraphObject.Fill,
    },
    visible(dataField),
    new go.Binding(
      'itemArray',
      dataField,
      (warnings: WarningPanelInterface[typeof dataField]) => warnings,
    ),
    {
      itemTemplate: WarningPanel(severity),
    },
  );

const WarningGroupTitle = (severity: Severity) =>
  $(
    go.Panel,
    'Horizontal',
    {
      alignment: go.Spot.LeftCenter,
    },
    $(go.Shape, 'Circle', {
      margin: new go.Margin(0, 8, 0, 0),
      alignment: new go.Spot(0, 0, 0, 1),
      width: 8,
      height: 8,
      fill: getSeverityColor(severity),
      strokeWidth: 0,
    }),
    $(
      go.TextBlock,
      {
        alignment: go.Spot.Left,
        text: getSeverityTitle(severity),
      },
      getFont({}),
    ),
  );

/**
 * Template for displaying warning messages
 */
export const WarningPanel = (severity: Severity) =>
  $(
    go.Panel,
    'Auto',
    {
      row: 0,
      column: 0,
      alignment: go.Spot.Left,
      margin: new go.Margin(0, 0, 0, 0), // Margin before this panel with Title and messages
    },
    $(
      go.Panel,
      'Vertical',
      WarningGroupTitle(severity),
      $(
        go.Panel,
        'Vertical',
        {
          margin: new go.Margin(4, 0, 8, 1), // Margin around the message, after the severity title
          alignment: go.Spot.Left,
          stretch: go.GraphObject.Fill,
          itemTemplate: $(
            go.Panel,
            'Horizontal',
            { alignment: go.Spot.Left, margin: new go.Margin(0, 0, 4, 0) },
            $(go.Shape, 'Circle', {
              margin: new go.Margin(0, 6, 0, 0),
              alignment: new go.Spot(0, 0, 0, 3),
              width: 2,
              height: 2,
            }),
            $(
              go.TextBlock,
              {
                stroke: getTextStroke(),
                maxSize: new go.Size(250, 80),
                minSize: new go.Size(0, 0),
              },
              getFont({}),
              new go.Binding('text', '', (data: string) => data),
            ),
          ),
        },
        new go.Binding('itemArray', '', (data) => data.warnings),
      ),
    ),
  );
